import { Avatar, Typography } from '@mui/material';
import { Link, type To } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  UserInfo_EnquiryFragment,
  UserInfo_UserFragment,
} from '@graphql/__generated__/graphql';
import {
  COMPACT_USER_AVATAR_SIZE,
  UserAvatar,
} from '@modules/_shared/components/UserAvatar';

export const USER_INFO_USER_FRAGMENT = gql(/* GraphQL */ `
  fragment UserInfo_user on users {
    id
    ...UserAvatar
    ...UserPhoneNumbers
    ...UserCardDisplayName
  }
`);

export const USER_INFO_ENQUIRY_FRAGMENT = gql(/* GraphQL */ `
  fragment UserInfo_enquiry on enquiries {
    id
    first_name
    last_name
    email_address
    phone_number
  }
`);

interface UserInfoProps {
  user?: UserInfo_UserFragment | null;
  enquiry?: UserInfo_EnquiryFragment | null;
  referrer?: null | string;
  enableLink?: boolean;
  to?: To;
  showPhone?: boolean;
  showEmail?: boolean;
  anonymize?: boolean;
  isSmall?: boolean;
  avatarSize?: number;
}

export const UserInfo = (props: UserInfoProps) => {
  const {
    referrer,
    enableLink = false,
    user,
    enquiry,
    to,
    showEmail = true,
    showPhone = false,
    isSmall = false,
  } = props;

  if (user != null && enquiry != null) {
    console.error('Only user or only enquiry should be passed to UserInfo');
    return null;
  }
  if (user == null && enquiry == null) {
    console.error('User or enquiry should be passed to UserInfo');
    return null;
  }

  const avatarSize =
    props.avatarSize ?? (isSmall ? COMPACT_USER_AVATAR_SIZE - 6 : undefined);
  const nameVariant = isSmall && props.avatarSize == null ? 'caption' : 'body2';

  const firstName = user?.first_name ?? enquiry?.first_name;
  let lastName = user?.last_name ?? enquiry?.last_name;
  if (props.anonymize) {
    lastName = lastName ? lastName?.charAt(0) + '.' : '';
  }
  const fullName = [firstName, lastName].join(' ');
  const email = user?.emails?.[0]?.email ?? enquiry?.email_address;
  const phone = user?.phone_numbers?.[0]?.number ?? enquiry?.phone_number;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        alignItems: 'center',
        gap: 8,
        height: '100%',
      }}
    >
      <UserAvatar
        user={
          user ?? {
            first_name: firstName,
            last_name: lastName,
          }
        }
        size={avatarSize}
      />
      {referrer != null && (
        <Avatar
          style={{
            height: '100%',
            width: 18,
            position: 'absolute',
            bottom: 0,
            right: 0,
          }}
          src={`https://www.google.com/s2/favicons?domain=${referrer}`}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {enableLink ? (
          <Link to={to ? to : `/users/${user?.id}`}>
            <Typography
              variant={nameVariant}
              noWrap
              sx={{
                fontWeight: 500,
              }}
            >
              {fullName}
            </Typography>
          </Link>
        ) : (
          <Typography variant={nameVariant} noWrap>
            {fullName}
          </Typography>
        )}
        {showEmail && !props.anonymize && (
          <Typography
            component="p"
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
              marginBottom: 0,
            }}
          >
            {email}
          </Typography>
        )}
        {showPhone && !props.anonymize && (
          <Typography
            component="p"
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
              marginBottom: 0,
            }}
          >
            {phone}
          </Typography>
        )}
      </div>
    </div>
  );
};
